import { Nav, Spinner, Tab } from "react-bootstrap";
import { Entry } from "interfaces/entry";
import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import SingleSelectionEntryList
  from "components/pool-details/entries/single-selection-entry-list/single-selection-entry-list";
import OrderedSelectionEntryList
  from "components/pool-details/entries/ordered-selection-entry-list/ordered-selection-entry-list";
import { PoolDetailsResponse } from "interfaces/pool-details-response";
import { useCallback, useEffect, useState } from "react";
import BetForm from "./bet-form";
import { Bet } from "interfaces/bet";
import { useAuth } from "hooks/auth";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { getGroupState, requestToJoinGroup } from "state/slices/group-slice";
import { POOL_STATUS, SKIP, XPOINT_NOT_FOUND } from "utils/constants";
import JoinGroupModal from "home/join-group-button/join-group-modal";
import { showSignIn } from "state/slices/login-modals-slice";
import useGeolocation from "hooks/useGeolocation";
import GeolocationStatus from "components/pool-details/geolocation/status-badge";
import GeolocationModal from "components/pool-details/geolocation/modal/modal";
import LoadingBetForm from "./loading-bet-form";
import { betTypeWin } from "interfaces/bet-type-name";
import { sortBetTypes } from "components/pool-details/bet-types";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus";
import { getIntegratedAppState } from "state/slices/integrated-app-slice";
import { postAttemptToPlaceABetMessage } from "utils/integration/iframe-messages";
import { CheckRequestStatus } from "@xpointtech/xpoint-js";
import ContestModal from "components/contests/contest-modal";
import { Contest, isBasicContest, isContestBettingBlocked, isPickPerPoolContest, picksCostForContestPick, remainingPicksForEntry } from "interfaces/leaderboard/contest.ts";
import { getContestEntryById, getFullContestEntryDetailsById } from "state/slices/contests-slice.ts";
import useContestPickedContestantIds from "hooks/useContestPickedContestantIds";

interface EntryListProps {
  pool: PoolDetailsResponse;
  setCurrentPoolType: any;
  onSuccessfulBetPlaced: (bet: Bet) => void;
  contest?: Contest;
  contestEntryId?: string;
}

// Pool Entry List
const EntryList = ({
                     pool,
                     setCurrentPoolType,
                     onSuccessfulBetPlaced,
                     contest,
                     contestEntryId
                   }: EntryListProps) => {
  const auth = useAuth();
  const [selectedEntry, setSelectedEntry] = useState<Entry>();
  const group = useAppSelector(getGroupState);
  const groupId = group.id;
  const integratedAppState = useAppSelector(getIntegratedAppState);
  const dispatch = useAppDispatch();
  const userHasJoinedGroup = group.status === UserGroupMembershipStatus.APPROVED;
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);
  const { geolocationStatus, checkStatus } = useGeolocation();
  const [showGeolocationModal, setShowGeolocationModal] = useState<CheckRequestStatus | typeof XPOINT_NOT_FOUND>(false);
  const [isFetchingEntry, setIsFetchingEntry] = useState(false);
  const [showJoinContestModal, setShowJoinContestModal] = useState(false);

  const contestEntryDetails = useAppSelector(state => getFullContestEntryDetailsById(state, contestEntryId));
  const contestEntry = useAppSelector(state => getContestEntryById(state, contestEntryId));
  // Picks remaining is derived from the entry, not the wallet balance, which
  // inflates when pool winnings are credited back into the entry.
  const remainingPicks = remainingPicksForEntry(contestEntry);
  const isBasic = isBasicContest(contest);

  // Basic-only: contestants this entry has already wagered on. Used to grey
  // them out across all bet types since backend rejects duplicate picks.
  const rawPickedIds = useContestPickedContestantIds(pool.id, pool.entries, contestEntryId);
  const isPoolAlreadyPicked = isPickPerPoolContest(contest) && rawPickedIds.size > 0;
  const isBettingBlocked = isContestBettingBlocked(contest, remainingPicks) || isPoolAlreadyPicked;
  const dimForContestBlock = isBettingBlocked && pool.status !== POOL_STATUS.SETTLED;
  const pickedContestantIds = isBasic ? rawPickedIds : new Set<string>();

  const allowGeolocationBadge =
    group.settings.enable_geolocation && auth.signedIn && userHasJoinedGroup;

  const orderedBetTypes = sortBetTypes(pool.pool_types);

  useEffect(() => {
    if (geolocationStatus === CheckRequestStatus.ALLOWED) {
      setShowGeolocationModal(false);
    }
  }, [geolocationStatus]);

  const contestGroupAndHasNotJoinedContest = group.isContestGroup && !contestEntryId;

  const selectBet = useCallback(async (entry: Entry) => {
    if (!entry) {
      // User unselected a contestant, hide the form
      setSelectedEntry(undefined);
      return;
    }
    if (isBettingBlocked) {
      return;
    }
    const geoResult = await checkStatus();

    setShowGeolocationModal(true);
    if (userHasJoinedGroup) {
      if (![CheckRequestStatus.ALLOWED, SKIP].includes(geoResult.status)) {
        setShowGeolocationModal(true);
      }
    }

    if (integratedAppState.isIntegratedApp) {
      postAttemptToPlaceABetMessage(pool.id);

      if (integratedAppState.userId) {
        setSelectedEntry(entry);
        setIsFetchingEntry(false);
      }
      return;
    }

    if (!auth.signedIn) {
      dispatch(showSignIn());
      return;
    }

    if (group.name && !userHasJoinedGroup) {
      setShowJoinGroupModal(true);
      return;
    }

    if (contestGroupAndHasNotJoinedContest) {
      setShowJoinContestModal(true);
      return;
    }

    setSelectedEntry(entry);
    setIsFetchingEntry(false);
  }, [dispatch, auth.signedIn, group.name, userHasJoinedGroup, pool.id, checkStatus, integratedAppState.isIntegratedApp, integratedAppState.userId, isBettingBlocked]);

  const memoizedSetIsFetchingEntry = useCallback(() => {
    // calculate if we should show loading state or not
    // const geoResult = await checkStatus();

    if (userHasJoinedGroup && auth.signedIn) {
      setIsFetchingEntry(true);
    } else {
      setIsFetchingEntry(false);
    }
  }, [userHasJoinedGroup, auth.signedIn]);

  return (
    <div style={{ opacity: dimForContestBlock ? 0.5 : 1 }}>
      {allowGeolocationBadge && (
        <GeolocationStatus
          status={geolocationStatus}
          showGeolocationModal={setShowGeolocationModal}
        />
      )}
      <Tab.Container
        id="bet-type-tabs"
        defaultActiveKey={orderedBetTypes[0]}
        onSelect={(tab) => {
          setCurrentPoolType(tab);
          setSelectedEntry(undefined);
        }}
      >
        {orderedBetTypes.length > 1 && (
          <Nav
            justify
            variant="pills"
            style={{
              paddingTop: 10,
              paddingBottom: 5,
              paddingLeft: 20,
              paddingRight: 20,
            }}
          >
            {orderedBetTypes.length > 1 &&
              orderedBetTypes.map((type) => {
                const cost = picksCostForContestPick(contest, type);
                const freshCount = (pool.contestants?.length ?? 0) - pickedContestantIds.size;
                const unaffordable = isBasic && remainingPicks < cost;
                const notEnoughFresh = isBasic && freshCount < cost;
                const unselectable = unaffordable || notEnoughFresh;
                const tooltip = unaffordable
                  ? `Uses ${cost} picks — you have ${remainingPicks}`
                  : notEnoughFresh
                  ? `Needs ${cost} different contestants — only ${freshCount} left`
                  : undefined;
                return (
                  <Nav.Item key={type} style={{ paddingRight: 5 }}>
                    <Nav.Link
                      eventKey={type}
                      disabled={unselectable}
                      title={tooltip}
                      style={{
                        textTransform: "capitalize",
                        cursor: unselectable ? "not-allowed" : "pointer",
                        opacity: unselectable ? 0.5 : undefined,
                      }}
                    >
                      <BlockyBoldText>{type}</BlockyBoldText>
                    </Nav.Link>
                  </Nav.Item>
                );
              })}
          </Nav>
        )}{" "}
        <Tab.Content style={{ position: "relative" }}>
          {contestEntryDetails?.loadingState === "loading" || contestEntryDetails?.loadingState === "idle" ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <Spinner />
            </div>
          ) : (
            orderedBetTypes.map((type) => {
              if (type === betTypeWin) {
                return (
                  <Tab.Pane key={type} eventKey={type}>
                    <SingleSelectionEntryList
                      contestants={pool.contestants}
                      entries={pool.entries}
                      selectBet={selectBet}
                      winningContestantIds={pool.winner_contestant_ids_places ? (pool.winner_contestant_ids_places[1] || []) : []}
                      poolStatus={pool.status}
                      selectedEntry={selectedEntry}
                      contestEntryId={contestEntryId}
                      pickedContestantIds={pickedContestantIds}
                    />
                  </Tab.Pane>
                );
              } else {
                return (
                  <Tab.Pane key={type} eventKey={type}>
                    <OrderedSelectionEntryList
                      contestants={pool.contestants}
                      selectBet={selectBet}
                      poolType={type}
                      poolId={pool.id}
                      pool={pool}
                      setIsFetchingEntry={memoizedSetIsFetchingEntry}
                      pickedContestantIds={pickedContestantIds}
                    />
                  </Tab.Pane>
                );
              }
            })
          )}
        </Tab.Content>
        {selectedEntry && (
          <div style={{ height: 163 }}>
          </div>
        )}
      </Tab.Container>
      {selectedEntry && (
        <BetForm
          entry={selectedEntry}
          pool={pool}
          onSuccessfulBetPlaced={onSuccessfulBetPlaced}
          contestEntryId={contestEntryId}
          contest={contest}
        />
      )}
      {isFetchingEntry && <LoadingBetForm />}
      <JoinGroupModal
        acceptAndJoin={() => {
          dispatch(requestToJoinGroup(groupId));
          setShowJoinGroupModal(false);
          if (contestGroupAndHasNotJoinedContest) {
            setShowJoinContestModal(true);
          }
        }}
        show={showJoinGroupModal}
        close={() => setShowJoinGroupModal(false)}
      />
      {group.settings.enable_geolocation && auth.signedIn && (
        <GeolocationModal
          show={showGeolocationModal && group.settings.enable_geolocation}
          hide={() => {
            setShowGeolocationModal(false);
            checkStatus();
          }}
        />
      )}
      {group.isContestGroup && contest &&
        <ContestModal
          contest={contest}
          show={showJoinContestModal}
          setShow={setShowJoinContestModal}
        />
      }
    </div>
  );
};

export default EntryList;
